// Standard Library Imports
import React, { useEffect, useState, useContext, useRef } from 'react';
//External library imports
import { toast } from 'react-toastify';
import { UilImport, UilInfoCircle } from '@iconscout/react-unicons';
import _ from 'lodash';
import moment from 'moment';
import { DateParam, StringParam } from 'use-query-params';

//Internal module imports
import { useLoader, useQueryParamWithDefault } from '../../../../hooks';
import economicsClasses from '../Economics.module.css';
import DateRangePicker from '../../../../components/Inputs/DateRangePicker/DateRangePicker';
import { DropdownComponent } from '../../../../components/Inputs/Input';
import Typography from '../../../../components/Typography/Typography';
import Table from '../../../../components/Table/Table';
import { RootEconomicsService } from '../../../../services/BiddingService/Root/EconomicsService';
import { usToEuCurrencyFormat } from '../../../../utils/currencyHelper';
import { CamelCaseToTextCase } from '../../../../utils/stringHelper';
import IconComponent from '../../../../components/Icon/Icon';
import { exportMultiSheetData } from '../../../../utils/downloadAsExcel';

const AREA_OPTIONS = [
    {
        label: 'DK1',
        value: 'DK1',
    },
    {
        label: 'DK2',
        value: 'DK2',
    },
];

const TYPE_OPTIONS = [
    {
        label: 'All',
        value: 'all',
    },
    {
        label: 'Activated Reserves',
        value: 'activatedReserves',
    },
    {
        label: 'Capacity Reserves',
        value: 'capacityReserves',
    },
    {
        label: 'Imbalance',
        value: 'imbalance',
    },
];

export const EsettSettlements = ({ cId }) => {
    const [startLoader, stopLoader] = useLoader();
    const [startDate, setStartDate] = useQueryParamWithDefault('startDate', DateParam, moment().startOf('month').toDate(), cId);
    const [endDate, setEndDate] = useQueryParamWithDefault('endDate', DateParam, moment().endOf('month').toDate(), cId);
    const [selectedArea, setSelectedArea] = useQueryParamWithDefault('area', StringParam, AREA_OPTIONS[0].value, cId);
    const [selectedType, setSelectedType] = useQueryParamWithDefault('type', StringParam, TYPE_OPTIONS[0].value, cId);
    const [data, setData] = useState({
        eSettTotalEnergyRevenue: 0,
        eSettTotalCapacityRevenue: 0,

        tgTotalEnergyRevenue: 0,
        tgTotalCapacityRevenue: 0,

        eSettTotalActivatedConsumption: 0,
        tgTotalActivatedConsumption: 0,

        eSettTotalCapacityConsumption: 0,
        tgTotalCapacityConsumption: 0,

        eSettTotalImbalance: 0,
        tgTotalImbalance: 0,

        eSettTotalImbalanceConsumption: 0,
        tgTotalImbalanceConsumption: 0,

        tableData: [],
    });

    useEffect(() => {
        if (startDate && endDate && selectedArea) getData();
    }, [selectedArea, selectedType]);

    const getData = (download = false) => {
        const successHandler = !download ? handleSuccess : handleDownload;
        const params = {
            startDate: moment(startDate).format('YYYY-MM-DD'),
            endDate: moment(endDate).format('YYYY-MM-DD'),
            area: selectedArea,
            type: selectedType,
            download: download,
        };
        RootEconomicsService.GetEsettSettlement(params, startLoader, successHandler, handleError, stopLoader);
    };

    const roundedNumber = (number = 0) => {
        return Math.round(number)?.toLocaleString('da-DK');
    };

    const handleSuccess = ({ data }) => {
        const temp = {
            eSettTotalEnergyRevenue: 0,
            eSettTotalCapacityRevenue: 0,
            tgTotalEnergyRevenue: 0,
            tgTotalCapacityRevenue: 0,
            eSettTotalActivatedConsumption: 0,
            tgTotalActivatedConsumption: 0,
            eSettTotalCapacityConsumption: 0,
            tgTotalCapacityConsumption: 0,
            eSettTotalImbalance: 0,
            tgTotalImbalance: 0,
            eSettTotalImbalanceConsumption: 0,
            tgTotalImbalanceConsumption: 0,
            tableData: [],
        };
        _.get(data, ['data', 'eSettData'], []).map((el) => {
            if (el._id === 'capacityReserves') {
                _.set(temp, ['eSettTotalCapacityRevenue'], roundedNumber(el?.totaAmount || 0), 0);
                _.set(temp, ['eSettTotalCapacityConsumption'], usToEuCurrencyFormat(Number(el?.totalConsumption || 0), 2), 0);
            } else if (el._id === 'activatedReserves') {
                _.set(temp, ['eSettTotalEnergyRevenue'], roundedNumber(el?.totaAmount || 0), 0);
                _.set(temp, ['eSettTotalActivatedConsumption'], usToEuCurrencyFormat(Number(el?.totalConsumption || 0), 2), 0);
            } else if (el._id === 'imbalance') {
                _.set(temp, ['eSettTotalImbalance'], roundedNumber(el?.totaAmount || 0), 0);
                _.set(temp, ['eSettTotalImbalanceConsumption'], usToEuCurrencyFormat(Number(el?.totalConsumption || 0), 2), 0);
            }

            if (el._id !== 'imbalance') {
                el.markets.map((item) => {
                    let quantity = 0;
                    let amount = 0;
                    _.get(data, ['data', 'tgData', 'markets'], []).map((tg) => {
                        if (tg.market === item.market) {
                            quantity = el._id === 'capacityReserves' ? tg.capacityConsumption : tg.energyConsumption / 1000;
                            amount = el._id === 'capacityReserves' ? tg.capacityRevenue : tg.energyRevenue;
                        }
                    });
                    temp.tableData.push({
                        type: CamelCaseToTextCase(el._id),
                        market: item.market,
                        quantity: usToEuCurrencyFormat(Number(quantity), 2),
                        amount: roundedNumber(amount),
                        esettQuantity: usToEuCurrencyFormat(Number(item.totalConsumption), 2),
                        esettAmount: roundedNumber(item.totaAmount),
                    });
                });
            } else {
                temp.tableData.push({
                    type: CamelCaseToTextCase(el._id),
                    market: '--',
                    quantity: usToEuCurrencyFormat(Number(_.get(data, ['data', 'tgData', 'totalImbalanceConsumption']) || 0), 2),
                    amount: roundedNumber(_.get(data, ['data', 'tgData', 'totalImbalanceCost']) || 0),
                    esettQuantity: usToEuCurrencyFormat(Number(el?.totalConsumption || 0), 2),
                    esettAmount: roundedNumber(el?.totaAmount),
                });
            }
        });
        const tgData = _.get(data, ['data', 'tgData']);

        _.set(temp, ['tgTotalEnergyRevenue'], roundedNumber(tgData?.totalEnergyRevenue || 0), 0);
        _.set(temp, ['tgTotalCapacityRevenue'], roundedNumber(tgData?.totalCapacityRevenue || 0), 0);
        _.set(temp, ['tgTotalActivatedConsumption'], usToEuCurrencyFormat(Number(tgData?.totalEnergyConsumption || 0) / 1000, 2), 0);
        _.set(temp, ['tgTotalCapacityConsumption'], usToEuCurrencyFormat(Number(tgData?.totalCapacityConsumption || 0), 2), 0);
        _.set(temp, ['tgTotalImbalanceConsumption'], usToEuCurrencyFormat(Number(tgData?.totalImbalanceConsumption || 0), 2), 0);
        _.set(temp, ['tgTotalImbalance'], roundedNumber(tgData?.totalImbalanceCost || 0), 0);

        setData(temp);
    };

    const handleDownload = ({ data }) => {
        const finalData = [];

        _.get(data, ['data', 'byDay'], []).forEach((el) => {
            const date = el.date;
            const eSettRows = _.get(el, ['eSettData'], []);
            const tgData = _.get(el, ['tgData'], {});
            const tgMarkets = _.get(tgData, ['markets'], []);

            eSettRows.forEach((settEl) => {
                if (settEl._id !== 'imbalance') {
                    _.get(settEl, ['markets'], []).forEach((item) => {
                        let quantity = 0;
                        let amount = 0;

                        tgMarkets.forEach((tg) => {
                            if (tg.market === item.market) {
                                quantity = settEl?._id === 'capacityReserves' ? tg.capacityConsumption : tg.energyConsumption / 1000;
                                amount = settEl?._id === 'capacityReserves' ? tg.capacityRevenue : tg.energyRevenue;
                            }
                        });

                        finalData.push({
                            date,
                            type: CamelCaseToTextCase(settEl?._id),
                            market: item?.market,
                            eSettQuantity: item?.totalConsumption || 0,
                            eSettAmount: Math.round(item.totaAmount || 0),
                            tgQuantity: quantity,
                            tgAmount: Math.round(amount),
                        });
                    });
                } else {
                    finalData.push({
                        date,
                        type: 'Imbalance',
                        market: '--',
                        eSettQuantity: settEl?.totalConsumption || 0,
                        eSettAmount: Math.round(settEl?.totaAmount || 0),
                        tgQuantity: tgData?.totalImbalanceConsumption || 0,
                        tgAmount: Math.round(tgData?.totalImbalanceCost || 0),
                    });
                }
            });
        });

        const imbalanceDetails = _.get(data, ['data', 'imbalanceDetails'], []);

        exportMultiSheetData(
            [
                {
                    data: finalData,
                    headers: ['date', 'type', 'market', 'eSettQuantity', 'eSettAmount', 'tgQuantity', 'tgAmount'],
                    sheetName: 'eSett Settlements',
                },
                {
                    data: imbalanceDetails,
                    headers: ['instanceName', 'imbalanceCost', 'imbalanceConsumption'],
                    sheetName: 'Imbalance Details',
                },
            ],
            'eSett Settlements'
        );
    };

    const handleError = (err) => {
        toast.error(err?.response?.data?.message);
    };

    return (
        <div>
            <div>
                <div className={economicsClasses.FilterBar}>
                    <div className={economicsClasses.FilterContainer}>
                        {/* left container */}
                        <div className={economicsClasses.SubContainer}>
                            <div className={economicsClasses.DropDownContainer}>
                                <label>Area</label>
                                <DropdownComponent
                                    name="area"
                                    options={AREA_OPTIONS}
                                    defaultValue={{ value: selectedArea }}
                                    onChange={(e) => {
                                        if (e.value !== selectedArea) setSelectedArea(e.value);
                                    }}
                                />
                            </div>
                            <div className={economicsClasses.DropDownContainer}>
                                <label>Type</label>
                                <DropdownComponent
                                    name="type"
                                    options={TYPE_OPTIONS}
                                    defaultValue={{ value: selectedType }}
                                    onChange={(e) => {
                                        if (e.value !== selectedType) setSelectedType(e.value);
                                    }}
                                />
                            </div>
                        </div>

                        {/* right container */}
                        <div className={economicsClasses.SubContainer}>
                            <div>
                                {startDate && endDate && (
                                    <DateRangePicker
                                        startDate={startDate}
                                        endDate={endDate}
                                        onChange={(date) => {
                                            setStartDate(moment(date.startDate).toDate());
                                            setEndDate(moment(date.endDate).toDate());
                                        }}
                                        style={{ height: '2.5vw', marginBottom: '-0.9vh' }}
                                        showLabels={true}
                                    />
                                )}
                            </div>

                            <div style={{ marginTop: '0.8vh' }} onClick={() => getData()}>
                                <button className="btn-primary">Submit</button>
                            </div>

                            <div style={{ marginTop: '2.5vh' }} onClick={() => getData(true)}>
                                <IconComponent icon={UilImport} tooltip={'Download'} size="1.5vw" />
                            </div>
                        </div>
                    </div>
                </div>

                <div className={economicsClasses.TableContainer}>
                    <div className={economicsClasses.FilterContainer} style={{ marginTop: '1vh' }}>
                        <div className={economicsClasses.SubContainer}>
                            <div className={economicsClasses.TableCount}>
                                TG EAM Volume :
                                <span>
                                    <Typography size="14" content={data.tgTotalActivatedConsumption + ' MWh'} />
                                </span>
                            </div>

                            <div className={economicsClasses.TableCount}>
                                eSett EAM Volume :
                                <span>
                                    <Typography size="14" content={data.eSettTotalActivatedConsumption + ' MWh'} />
                                </span>
                            </div>

                            <div className={economicsClasses.TableCount}>
                                TG CM Volume :
                                <span>
                                    <Typography size="14" content={data.tgTotalCapacityConsumption + ' MW'} />
                                </span>
                            </div>

                            <div className={economicsClasses.TableCount}>
                                eSett CM Volume :
                                <span>
                                    <Typography size="14" content={data.eSettTotalCapacityConsumption + ' MW'} />
                                </span>
                            </div>

                            <div className={economicsClasses.TableCount}>
                                TG Imbalance Volume :
                                <span>
                                    <Typography size="14" content={data?.tgTotalImbalanceConsumption || 0 + ' MW'} />
                                </span>
                            </div>

                            <div className={economicsClasses.TableCount}>
                                eSett Imbalance Volume :
                                <span>
                                    <Typography size="14" content={data?.eSettTotalImbalanceConsumption || 0 + ' MW'} />
                                </span>
                            </div>

                            <div className={economicsClasses.TableCount}>
                                Total Count :
                                <span>
                                    <Typography size="14" content={data?.tableData?.length || 0} />
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className={economicsClasses.FilterContainer} style={{ marginTop: '1vh' }}>
                        <div className={economicsClasses.SubContainer}>
                            <div className={economicsClasses.TableCount}>
                                TG Total Revenue EAM :
                                <span>
                                    <Typography size="14" content={data.tgTotalEnergyRevenue + ' €'} />
                                </span>
                            </div>

                            <div className={economicsClasses.TableCount}>
                                eSett Total Revenue EAM :
                                <span>
                                    <Typography size="14" content={data.eSettTotalEnergyRevenue + ' €'} />
                                </span>
                            </div>

                            <div className={economicsClasses.TableCount}>
                                TG Total Revenue CM :
                                <span>
                                    <Typography size="14" content={data.tgTotalCapacityRevenue + ' €'} />
                                </span>
                            </div>

                            <div className={economicsClasses.TableCount}>
                                eSett Total Revenue CM :
                                <span>
                                    <Typography size="14" content={data.eSettTotalCapacityRevenue + ' €'} />
                                </span>
                            </div>

                            <div className={economicsClasses.TableCount}>
                                TG Total Imbalance :
                                <span>
                                    <Typography size="14" content={data.tgTotalImbalance + ' €'} />
                                </span>
                            </div>

                            <div className={economicsClasses.TableCount}>
                                eSett Total Imbalance :
                                <span>
                                    <Typography size="14" content={data.eSettTotalImbalance + ' €'} />
                                </span>
                            </div>
                        </div>
                    </div>

                    <Table
                        head={['Type', 'Market', 'Quantity (MW)', 'Amount (€)', 'eSett Quantity (MWh)', 'eSett Amount (€)']}
                        keys={['type', 'market', 'quantity', 'amount', 'esettQuantity', 'esettAmount']}
                        data={data.tableData}
                    />
                </div>
            </div>
        </div>
    );
};
